/**
 * Dummy Top Performer data for local development, shaped like the real endpoints' responses.
 *
 * Flip `USE_TOP_PERFORMER_MOCK` to serve it instead of `getTopPerformerStatus` /
 * `getPerformanceTierConfiguration` — the screens keep using the same hooks either way.
 */

import {
  CurrentPerformerStatus,
  EvaluationMonth,
  GetTopPerformerMonthlyReportResponse,
  PerformanceTierConfiguration,
  GetTopPerformerStatus200ResponseData,
  InProgressPerformerStatus,
} from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal/models';

// Annotated as `boolean` on purpose: a literal type would make the unused branch of every
// `USE_TOP_PERFORMER_MOCK ? ... : ...` dead code and stop it being type-checked.
export const USE_TOP_PERFORMER_MOCK: boolean = false;

// Anchored to the current month so the month names and the days-left countdown stay believable.
const today = new Date();
const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);

const IN_PROGRESS_MONTH: EvaluationMonth = {
  year: today.getFullYear(),
  month: today.getMonth() + 1,
};

const CURRENT_MONTH: EvaluationMonth = {
  year: previousMonth.getFullYear(),
  month: previousMonth.getMonth() + 1,
};

const IN_PROGRESS_STATUS_MOCKS: { [key: string]: InProgressPerformerStatus } = {
  good: {
    type: 'eligible',
    month: IN_PROGRESS_MONTH,
    tier: 'good',
    goal_statuses: [
      { id: 'unfulfilled_orders', state: 'completed', current_value: 0.001 },
      { id: 'orders_with_complaints', state: 'completed', current_value: 0.003 },
      { id: 'unavailable_rate', state: 'completed', current_value: 0.021 },
      { id: 'order_delay_rate', state: 'unfulfilled', current_value: 0.2 },
      { id: 'rating_and_feedback', state: 'completed', current_value: 4.9 },
      { id: 'menu_score', state: 'completed', current_value: 0.91 },
    ],
  },
};

const CURRENT_STATUS_MOCKS: { [key: string]: CurrentPerformerStatus } = {
  not_evaluated: {
    type: 'not_evaluated',
  },
  ineligible: {
    type: 'ineligible',
    month: CURRENT_MONTH,
    not_eligible_reasons: [{ reason: 'not_enough_orders', value: 14, expected_value: 20 }],
  },
  needs_improvement: {
    type: 'eligible',
    month: CURRENT_MONTH,
    tier: 'needs_improvement',
    goal_statuses: [
      { id: 'unfulfilled_orders', state: 'unfulfilled', current_value: 0.001 },
      { id: 'orders_with_complaints', state: 'unfulfilled', current_value: 0.003 },
      { id: 'unavailable_rate', state: 'completed', current_value: 0.021 },
      { id: 'order_delay_rate', state: 'unfulfilled', current_value: 0.2 },
      { id: 'rating_and_feedback', state: 'completed', current_value: 4.9 },
      { id: 'menu_score', state: 'completed', current_value: 0.91 },
    ],
  },
  good: {
    type: 'eligible',
    month: CURRENT_MONTH,
    tier: 'good',
    goal_statuses: [
      { id: 'unfulfilled_orders', state: 'completed', current_value: 0.001 },
      { id: 'orders_with_complaints', state: 'completed', current_value: 0.003 },
      { id: 'unavailable_rate', state: 'completed', current_value: 0.021 },
      { id: 'order_delay_rate', state: 'unfulfilled', current_value: 0.2 },
      { id: 'rating_and_feedback', state: 'completed', current_value: 4.9 },
      { id: 'menu_score', state: 'completed', current_value: 0.91 },
    ],
  },
  top: {
    type: 'eligible',
    month: CURRENT_MONTH,
    tier: 'top',
    goal_statuses: [
      { id: 'unfulfilled_orders', state: 'completed', current_value: 0.001 },
      { id: 'orders_with_complaints', state: 'completed', current_value: 0.003 },
      { id: 'unavailable_rate', state: 'completed', current_value: 0.021 },
      { id: 'order_delay_rate', state: 'completed', current_value: 0.2 },
      { id: 'rating_and_feedback', state: 'completed', current_value: 4.9 },
      { id: 'menu_score', state: 'completed', current_value: 0.91 },
    ],
  },
};

export const TOP_PERFORMER_STATUS_MOCK: GetTopPerformerStatus200ResponseData = {
  type: 'available',
  in_progress: IN_PROGRESS_STATUS_MOCKS.good,
  current: CURRENT_STATUS_MOCKS.good,
};

export const USE_BOLT_STAR_MONTHLY_UPDATE_MOCK: boolean = false;

export const MONTHLY_UPDATE_MOCK: GetTopPerformerMonthlyReportResponse = {
  new_report_available: true,
  tier: 'good',
};

export const PERFORMANCE_TIER_CONFIGURATION_MOCK: PerformanceTierConfiguration = {
  benefits: [
    { id: 'top_performer_badge', unlock_tier: 'top' },
    { id: 'featured_placement', unlock_tier: 'top' },
    { id: 'smart_promotions', unlock_tier: 'good' },
  ],
  goals: [
    {
      id: 'unfulfilled_orders',
      tiers: {
        poor: { goal_type: 'above', goal_value: 0.025, value_type: 'percentage' },
        needs_improvement: { goal_type: 'below', goal_value: 0.025, value_type: 'percentage' },
        good: { goal_type: 'below', goal_value: 0.03, value_type: 'percentage' },
        top: { goal_type: 'below', goal_value: 0.002, value_type: 'percentage' },
      },
    },
    {
      id: 'orders_with_complaints',
      tiers: {
        poor: { goal_type: 'above', goal_value: 0.015, value_type: 'percentage' },
        needs_improvement: { goal_type: 'below', goal_value: 0.015, value_type: 'percentage' },
        good: { goal_type: 'below', goal_value: 0.02, value_type: 'percentage' },
        top: { goal_type: 'below', goal_value: 0.005, value_type: 'percentage' },
      },
    },
    {
      id: 'unavailable_rate',
      tiers: {
        poor: { goal_type: 'above', goal_value: 0.09, value_type: 'percentage' },
        needs_improvement: { goal_type: 'below', goal_value: 0.09, value_type: 'percentage' },
        good: { goal_type: 'below', goal_value: 0.1, value_type: 'percentage' },
        top: { goal_type: 'below', goal_value: 0.03, value_type: 'percentage' },
      },
    },
    {
      id: 'order_delay_rate',
      tiers: {
        poor: { goal_type: 'above', goal_value: 0.13, value_type: 'percentage' },
        needs_improvement: { goal_type: 'below', goal_value: 0.13, value_type: 'percentage' },
        good: { goal_type: 'below', goal_value: 0.15, value_type: 'percentage' },
        top: { goal_type: 'below', goal_value: 0.05, value_type: 'percentage' },
      },
    },
    {
      id: 'rating_and_feedback',
      tiers: {
        poor: { goal_type: 'below', goal_value: 3.8, value_type: 'numeric' },
        needs_improvement: { goal_type: 'above', goal_value: 3.8, value_type: 'numeric' },
        good: { goal_type: 'above', goal_value: 4, value_type: 'numeric' },
        top: { goal_type: 'above', goal_value: 4.7, value_type: 'numeric' },
      },
    },
    {
      id: 'menu_score',
      tiers: {
        poor: { goal_type: 'below', goal_value: 0.55, value_type: 'percentage' },
        needs_improvement: { goal_type: 'above', goal_value: 0.55, value_type: 'percentage' },
        good: { goal_type: 'above', goal_value: 0.6, value_type: 'percentage' },
        top: { goal_type: 'above', goal_value: 0.85, value_type: 'percentage' },
      },
    },
  ],
};
